import { useStore } from "@/stores/useStore";
import { cn } from "@/lib/utils";
import shopHeroLogo from "../../assets/shop-hero-logo-horizontal.svg";
import { Button } from "../Base/Button";
import { Menu as MenuIcon } from "lucide-react";
import { PropsWithChildren, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HEADER_HEIGHT_PX } from "@/lib/layout";

export interface HeaderProps extends PropsWithChildren {
  onMenuClick: () => void;
}

export const Header = ({ onMenuClick, children }: HeaderProps) => {
  const { logoUrl, authState, shopId } = useStore();
  const [shopLogoFailed, setShopLogoFailed] = useState(false);

  // logoUrl resolves asynchronously and changes on shop switch; re-arm so a
  // failure on one URL doesn't permanently suppress the next shop's logo.
  useEffect(() => setShopLogoFailed(false), [logoUrl]);

  const showShopLogo = Boolean(logoUrl) && !shopLogoFailed;

  return (
    <div
      className="flex flex-shrink-0 flex-row items-center justify-between bg-[#F1F5F9] px-2 py-0.5 text-sm shadow-lg"
      // Applied rather than a Tailwind class so the board's height reservation
      // and this header can only ever change together (WEBV3-256).
      style={{ height: HEADER_HEIGHT_PX }}
    >
      <Button
        variant="ghost"
        size="icon"
        onClick={onMenuClick}
        className="m-2 text-[#244B8F]"
        disabled={authState !== "authenticated" && authState !== "unverified"}
      >
        <MenuIcon className="size-5" />
      </Button>
      <div className="flex h-full items-center">
        <Link to={shopId ? `/${shopId}` : "/"} aria-label="Go to dashboard">
          <img
            src={showShopLogo ? logoUrl : shopHeroLogo}
            alt="logo"
            onError={() => setShopLogoFailed(true)}
            // The fallback takes a definite h-6, not max-h-6: sized only by
            // max-height inside the shrink-to-fit <Link>, an image with no
            // intrinsic width resolves to a 0x0 box (WEBV3-168). A shop's own
            // logo is a raster with real dimensions, so max-h-9 caps large
            // ones without upscaling small ones.
            className={cn("w-auto", showShopLogo ? "max-h-9" : "h-6")}
          />
        </Link>
      </div>
      <div className="flex-grow"></div>
      {children}
    </div>
  );
};
